/* Aviso del inicio: solicitudes a RRHH esperando la aprobacion del jefe.
 *
 * - No se muestra nada si el jefe no tiene nada pendiente. Es a proposito: el
 *   inicio ya tiene varias tarjetas y la mayoria de los usuarios nunca van a
 *   ser el jefe designado de una solicitud.
 * - Al tocar abre la lista; al elegir una, se ve el detalle completo (quien la
 *   pidio, a quien afecta y los campos del motivo) y se aprueba o rechaza con
 *   comentario.
 *
 * Quien puede resolver lo decide EMSYS: la solicitud guarda la cedula del jefe
 * que salio de la estructura de operaciones y solo devuelve lo que le toca a
 * cada uno. AppEM es la UNICA pantalla de aprobacion del jefe (en EMSYS web no
 * existe), y no se le exige usuario de EMSYS. Aprobar no cierra la solicitud,
 * la pasa a gestion de RRHH.
 */
import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonModal,
  IonNote,
  IonSpinner,
  IonTextarea,
  IonTitle,
  IonToolbar,
  useIonToast,
} from "@ionic/react";
import { Typography } from "@material-ui/core";
import { arrowBackOutline, checkmarkOutline, closeOutline } from "ionicons/icons";
import { useCallback, useEffect, useState } from "react";

import { EvaluacionResumen } from "src/components/SolicitudRRHHEvaluacion";
import {
  DecisionJefe,
  SolicitudDetalle,
  SolicitudFila,
  listarPendientesJefe,
  obtenerSolicitud,
  resolverComoJefe,
} from "src/services/solicitudesRRHH";

const SolicitudesAprobarCard: React.FC = () => {
  const [present] = useIonToast();
  const [items, setItems] = useState<SolicitudFila[]>([]);
  const [cargando, setCargando] = useState(true);
  const [abierto, setAbierto] = useState(false);

  // Detalle de la que se esta revisando. null = se muestra la lista.
  const [detalle, setDetalle] = useState<SolicitudDetalle | null>(null);
  const [cargandoDetalle, setCargandoDetalle] = useState(false);
  const [comentario, setComentario] = useState("");
  const [error, setError] = useState("");
  const [resolviendo, setResolviendo] = useState<DecisionJefe | null>(null);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      setItems(await listarPendientesJefe());
    } catch {
      // Silencio a proposito: si falla, el inicio no muestra el aviso en vez
      // de mostrar un error que el usuario no puede resolver.
      setItems([]);
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  const abrirDetalle = async (id: number) => {
    setCargandoDetalle(true);
    setComentario("");
    setError("");
    try {
      setDetalle(await obtenerSolicitud(id));
    } catch (err) {
      const msg = err instanceof Error ? err.message : "No se pudo cargar";
      present({ message: msg, duration: 3500, color: "danger" });
    } finally {
      setCargandoDetalle(false);
    }
  };

  const volverALaLista = () => {
    setDetalle(null);
    setComentario("");
    setError("");
  };

  const resolver = async (decision: DecisionJefe) => {
    if (!detalle) return;
    const texto = comentario.trim();
    if (decision === "RECHAZAR" && !texto) {
      setError("El comentario es obligatorio al rechazar.");
      return;
    }
    setResolviendo(decision);
    setError("");
    try {
      await resolverComoJefe(detalle.id, decision, texto);
      present({
        message:
          decision === "APROBAR"
            ? `Solicitud #${detalle.id} aprobada, pasa a RRHH`
            : `Solicitud #${detalle.id} rechazada`,
        duration: 3500,
        color: decision === "APROBAR" ? "success" : "medium",
      });
      setDetalle(null);
      await cargar();
      // Si era la ultima, se cierra: la tarjeta va a desaparecer del inicio.
      if (items.length <= 1) setAbierto(false);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "No se pudo resolver";
      setError(msg);
    } finally {
      setResolviendo(null);
    }
  };

  if (cargando) return null;
  if (!items.length) return null;

  const bloqueado = resolviendo !== null;

  return (
    <div className="container mt-1">
      <div
        onClick={() => setAbierto(true)}
        style={{
          cursor: "pointer",
          background: "#fff",
          borderRadius: 10,
          borderLeft: "6px solid var(--garnet, #910434)",
          boxShadow: "0 1px 4px rgba(0,0,0,0.15)",
          padding: "12px 14px",
          marginBottom: 10,
        }}
      >
        <Typography
          variant="caption"
          style={{ color: "var(--garnet, #910434)", fontWeight: 700 }}
        >
          ✍️ SOLICITUDES POR APROBAR
        </Typography>
        <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
          {items.length === 1
            ? "Tenés 1 solicitud esperando tu aprobación"
            : `Tenés ${items.length} solicitudes esperando tu aprobación`}
        </Typography>
        <Typography
          variant="caption"
          style={{ color: "var(--garnet, #910434)", fontWeight: 600 }}
        >
          Tocá para revisar →
        </Typography>
      </div>

      <IonModal isOpen={abierto} onDidDismiss={() => setAbierto(false)}>
        <IonHeader>
          <IonToolbar>
            {detalle && (
              <IonButtons slot="start">
                <IonButton onClick={volverALaLista} disabled={bloqueado}>
                  <IonIcon icon={arrowBackOutline} slot="icon-only" />
                </IonButton>
              </IonButtons>
            )}
            <IonTitle style={{ fontSize: 16 }}>
              {detalle ? `Solicitud #${detalle.id}` : "Por aprobar"}
            </IonTitle>
            <IonButtons slot="end">
              <IonButton onClick={() => setAbierto(false)} disabled={bloqueado}>
                Cerrar
              </IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>

        <IonContent>
          {cargandoDetalle && (
            <div style={{ padding: 24, textAlign: "center" }}>
              <IonSpinner name="dots" />
            </div>
          )}

          {!cargandoDetalle && !detalle && (
            <IonList lines="full">
              {items.map((s) => (
                <IonItem
                  key={s.id}
                  button
                  detail
                  onClick={() => void abrirDetalle(s.id)}
                >
                  <IonLabel className="ion-text-wrap">
                    <h2 style={{ fontWeight: 600 }}>{s.motivo_texto}</h2>
                    <p>
                      <IonNote color="medium">Colaborador: </IonNote>
                      {s.colaborador || "-"}
                    </p>
                    <p>
                      <IonNote color="medium">Pidió: </IonNote>
                      {s.solicitante} · {s.fecha_registro}
                    </p>
                  </IonLabel>
                </IonItem>
              ))}
            </IonList>
          )}

          {!cargandoDetalle && detalle && (
            <>
              <IonList lines="full">
                <IonItem lines="full">
                  <IonLabel className="ion-text-wrap">
                    <IonNote color="medium">Motivo</IonNote>
                    <p style={{ color: "var(--ion-text-color)", fontWeight: 600 }}>
                      {detalle.motivo_texto}
                    </p>
                  </IonLabel>
                </IonItem>
                <IonItem lines="full">
                  <IonLabel className="ion-text-wrap">
                    <IonNote color="medium">Solicitado por</IonNote>
                    <p style={{ color: "var(--ion-text-color)" }}>
                      {detalle.solicitante} · {detalle.fecha_registro}
                    </p>
                  </IonLabel>
                </IonItem>
                {(detalle.colaborador_nombre || detalle.colaborador_legajo) && (
                  <IonItem lines="full">
                    <IonLabel className="ion-text-wrap">
                      <IonNote color="medium">Colaborador afectado</IonNote>
                      <p style={{ color: "var(--ion-text-color)" }}>
                        {detalle.colaborador_nombre}
                      </p>
                      <p style={{ fontSize: "0.8rem" }}>
                        Legajo {detalle.colaborador_legajo || "-"} · CI{" "}
                        {detalle.colaborador_cedula || "-"}
                      </p>
                    </IonLabel>
                  </IonItem>
                )}
                {detalle.ps_nombre && (
                  <IonItem lines="full">
                    <IonLabel className="ion-text-wrap">
                      <IonNote color="medium">Punto de Servicio</IonNote>
                      <p style={{ color: "var(--ion-text-color)" }}>
                        {detalle.ps_codigo} - {detalle.ps_nombre}
                      </p>
                    </IonLabel>
                  </IonItem>
                )}
                {detalle.campos.map((c) => (
                  <IonItem key={c.etiqueta} lines="full">
                    <IonLabel className="ion-text-wrap">
                      <IonNote color="medium">{c.etiqueta}</IonNote>
                      <p
                        style={{
                          color: "var(--ion-text-color)",
                          whiteSpace: "pre-line",
                        }}
                      >
                        {c.valor}
                      </p>
                    </IonLabel>
                  </IonItem>
                ))}
                {/* Los dos motivos que pasan por el jefe son los que exigen
                    la evaluacion: es lo que respalda el despido. */}
                {detalle.evaluacion && (
                  <IonItem lines="full">
                    <IonLabel className="ion-text-wrap">
                      <IonNote color="medium">Evaluación de desempeño</IonNote>
                      <div style={{ marginTop: 4 }}>
                        <EvaluacionResumen evaluacion={detalle.evaluacion} compacta />
                      </div>
                    </IonLabel>
                  </IonItem>
                )}
                {detalle.adjuntos.length > 0 && (
                  <IonItem lines="full">
                    <IonLabel className="ion-text-wrap">
                      <IonNote color="medium">Adjuntos</IonNote>
                      {detalle.adjuntos.map((a) => (
                        <p key={a.id} style={{ fontSize: "0.8rem" }}>
                          {a.nombre_archivo}
                        </p>
                      ))}
                    </IonLabel>
                  </IonItem>
                )}
              </IonList>

              <IonItem lines="none">
                <IonLabel position="stacked">
                  Comentario
                  <IonNote color="medium"> (obligatorio al rechazar)</IonNote>
                </IonLabel>
                <IonTextarea
                  value={comentario}
                  rows={3}
                  autoGrow
                  disabled={bloqueado}
                  onIonChange={(e) => setComentario(e.detail.value || "")}
                />
              </IonItem>

              {error && (
                <IonItem lines="none">
                  <IonNote color="danger">{error}</IonNote>
                </IonItem>
              )}

              <div style={{ display: "flex", gap: 8, padding: 16 }}>
                <IonButton
                  expand="block"
                  color="success"
                  style={{ flex: 1 }}
                  disabled={bloqueado}
                  onClick={() => void resolver("APROBAR")}
                >
                  {resolviendo === "APROBAR" ? (
                    <IonSpinner name="dots" />
                  ) : (
                    <>
                      <IonIcon icon={checkmarkOutline} slot="start" />
                      Aprobar
                    </>
                  )}
                </IonButton>
                <IonButton
                  expand="block"
                  color="danger"
                  style={{ flex: 1 }}
                  disabled={bloqueado}
                  onClick={() => void resolver("RECHAZAR")}
                >
                  {resolviendo === "RECHAZAR" ? (
                    <IonSpinner name="dots" />
                  ) : (
                    <>
                      <IonIcon icon={closeOutline} slot="start" />
                      Rechazar
                    </>
                  )}
                </IonButton>
              </div>

              <IonNote
                color="medium"
                className="ion-padding-start"
                style={{ display: "block", fontSize: "0.8rem", paddingBottom: 16 }}
              >
                Al aprobar, la solicitud pasa a gestión de RRHH.
              </IonNote>
            </>
          )}
        </IonContent>
      </IonModal>
    </div>
  );
};

export default SolicitudesAprobarCard;
